<template>
  <v-container fluid class="presentation-editor-page">
    <div class="editor-header">
      <div>
        <p class="eyebrow">Editor HTML</p>
        <h1>Apresentacoes</h1>
        <p class="subtitle">
          Edite o HTML da apresentacao e abra o preview em uma nova guia.
        </p>
      </div>
      <div class="header-actions">
        <v-btn text color="primary" to="/apresentacoes">
          <v-icon left>mdi-view-grid</v-icon>
          Catalogo
        </v-btn>
        <v-btn color="primary" depressed :href="currentEntry" target="_blank" rel="noopener" :disabled="!currentEntry">
          <v-icon left>mdi-open-in-new</v-icon>
          Abrir publicada
        </v-btn>
      </div>
    </div>

    <v-alert v-if="error" type="error" outlined class="mb-4">
      {{ error }}
    </v-alert>

    <v-row dense class="editor-toolbar">
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedSlug"
          :items="items"
          item-text="titulo"
          item-value="slug"
          label="Apresentacao"
          outlined
          dense
          hide-details
          :loading="loadingManifest"
          @change="loadPresentation"
        />
      </v-col>
      <v-col cols="12" md="8" class="toolbar-actions">
        <v-btn depressed @click="reloadPresentation" :loading="loadingPresentation">
          <v-icon left>mdi-refresh</v-icon>
          Recarregar
        </v-btn>
        <v-btn depressed @click="copyHtml" :disabled="!html">
          <v-icon left>mdi-content-copy</v-icon>
          Copiar
        </v-btn>
        <v-btn color="primary" depressed @click="openPreviewTab" :disabled="!html || !selectedSlug">
          <v-icon left>mdi-open-in-new</v-icon>
          Abrir preview
        </v-btn>
        <v-btn
          :color="splitPreview ? 'primary' : undefined"
          :outlined="!splitPreview"
          depressed
          @click="toggleSplitPreview"
          :disabled="!html || !selectedSlug"
        >
          <v-icon left>{{ splitPreview ? "mdi-view-agenda-outline" : "mdi-view-split-vertical" }}</v-icon>
          {{ splitPreview ? "Ocultar preview" : "Tela dividida" }}
        </v-btn>
        <v-btn color="primary" outlined @click="save" :loading="saving" :disabled="!html || !selectedSlug">
          <v-icon left>mdi-content-save</v-icon>
          Salvar
        </v-btn>
        <v-btn color="primary" depressed @click="openSaveAs" :disabled="!html">
          <v-icon left>mdi-content-save-plus</v-icon>
          Salvar como
        </v-btn>
        <v-btn depressed @click="downloadZip" :loading="downloading" :disabled="!selectedSlug">
          <v-icon left>mdi-folder-download</v-icon>
          Baixar ZIP
        </v-btn>
        <v-btn depressed @click="$refs.zipInput.click()" :loading="uploading" :disabled="!selectedSlug">
          <v-icon left>mdi-folder-upload</v-icon>
          Enviar ZIP
        </v-btn>
        <input ref="zipInput" class="d-none" type="file" accept=".zip,application/zip" @change="uploadZip" />
      </v-col>
    </v-row>

    <v-row dense class="workspace" :class="{ 'workspace--split': splitPreview }">
      <v-col cols="12" :lg="splitPreview ? 6 : 12" class="pane">
        <div class="pane-title">
          <span>Codigo HTML</span>
          <small>{{ html.length | number }} caracteres</small>
        </div>
        <textarea ref="editor" />
      </v-col>

      <v-col v-if="splitPreview" cols="12" lg="6" class="pane preview-pane">
        <div class="pane-title">
          <span>Preview</span>
          <small>Atualizacao automatica</small>
        </div>
        <div class="preview-shell">
          <iframe
            title="Preview da apresentacao"
            sandbox="allow-scripts allow-forms allow-popups allow-modals"
            :srcdoc="previewHtml"
          />
        </div>
      </v-col>
    </v-row>

    <v-row dense class="diagnostics">
      <v-col v-for="item in diagnostics" :key="item.label" cols="6" md="3">
        <v-card outlined class="diagnostic-card">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="saveDialog" max-width="640">
      <v-card>
        <v-card-title>Salvar como nova versao</v-card-title>
        <v-card-text>
          <v-text-field v-model="saveForm.titulo" label="Titulo" outlined dense />
          <v-text-field v-model="saveForm.slug" label="Slug" outlined dense hint="Usado na URL da apresentacao" persistent-hint />
          <v-textarea v-model="saveForm.descricao" label="Descricao" outlined rows="3" />
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="saveForm.versao" label="Versao" outlined dense />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="saveForm.tipo" label="Tipo" outlined dense />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="saveDialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="saveAs">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :timeout="5000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">Fechar</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import CodeMirror from "codemirror";
import { saveAs as saveFile } from "file-saver";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/material-darker.css";
import "codemirror/mode/xml/xml";
import "codemirror/mode/javascript/javascript";
import "codemirror/mode/css/css";
import "codemirror/mode/htmlmixed/htmlmixed";

export default {
  name: "ApresentacoesEditor",
  data() {
    return {
      editor: null,
      items: [],
      selectedSlug: "",
      html: "",
      previewHtml: "",
      splitPreview: false,
      loadingManifest: false,
      loadingPresentation: false,
      saving: false,
      downloading: false,
      uploading: false,
      error: "",
      previewTimer: null,
      saveDialog: false,
      saveForm: {
        titulo: "",
        slug: "",
        descricao: "",
        versao: "",
        tipo: "Apresentacao",
      },
      snackbar: {
        show: false,
        text: "",
      },
    };
  },
  computed: {
    currentItem() {
      return this.items.find((item) => item.slug === this.selectedSlug) || {};
    },
    currentEntry() {
      return this.currentItem.entry || "";
    },
    diagnostics() {
      const html = this.html || "";
      return [
        { label: "Slides", value: (html.match(/class=["'][^"']*slide/g) || []).length },
        { label: "Imagens/GIFs", value: (html.match(/<img\b/gi) || []).length },
        { label: "CSS", value: /<style[\s>]/i.test(html) ? "sim" : "nao" },
        { label: "JavaScript", value: /<script[\s>]/i.test(html) ? "sim" : "nao" },
      ];
    },
  },
  mounted() {
    this.setupEditor();
    this.loadManifest();
  },
  beforeDestroy() {
    window.clearTimeout(this.previewTimer);
    if (this.editor) {
      this.editor.toTextArea();
    }
  },
  methods: {
    setupEditor() {
      this.editor = CodeMirror.fromTextArea(this.$refs.editor, {
        mode: "htmlmixed",
        theme: "material-darker",
        lineNumbers: true,
        lineWrapping: true,
        tabSize: 2,
        indentUnit: 2,
        viewportMargin: 80,
      });
      this.editor.on("change", () => {
        this.html = this.editor.getValue();
        this.schedulePreview();
      });
    },
    async loadManifest() {
      this.loadingManifest = true;
      this.error = "";
      try {
        const response = await this.$http.get("/api/apresentacoes-editor/manifest");
        this.items = Array.isArray(response.data.apresentacoes)
          ? response.data.apresentacoes
          : [];
        this.selectedSlug =
          this.$route.params.slug ||
          (this.items.find((item) => item.slug === "ssdt-painel-rr-contrato-nacional-v3") || this.items[0] || {}).slug ||
          "";
        if (this.selectedSlug) {
          await this.loadPresentation();
        }
      } catch (err) {
        this.error = this.extractError(err, "Nao foi possivel carregar o catalogo.");
      } finally {
        this.loadingManifest = false;
      }
    },
    async loadPresentation() {
      if (!this.selectedSlug) {
        return;
      }
      this.loadingPresentation = true;
      this.error = "";
      try {
        const response = await this.$http.get(`/api/apresentacoes-editor/${this.selectedSlug}`);
        this.html = response.data.html || "";
        if (this.editor && this.editor.getValue() !== this.html) {
          this.editor.setValue(this.html);
        }
        this.updatePreview();
        if (this.$route.params.slug !== this.selectedSlug) {
          this.$router.replace({ name: "apresentacoes-editor", params: { slug: this.selectedSlug } });
        }
      } catch (err) {
        this.error = this.extractError(err, "Nao foi possivel carregar a apresentacao.");
      } finally {
        this.loadingPresentation = false;
      }
    },
    reloadPresentation() {
      return this.loadPresentation();
    },
    schedulePreview() {
      window.clearTimeout(this.previewTimer);
      this.previewTimer = window.setTimeout(() => this.updatePreview(), 300);
    },
    updatePreview() {
      this.previewHtml = this.withPreviewBase(this.html, this.selectedSlug);
    },
    withPreviewBase(html, slug) {
      const apiBase = String((this.$http && this.$http.defaults.baseURL) || window.location.origin).replace(/\/+$/, "");
      const base = `<base href="${apiBase}/api/apresentacoes-editor/file/${encodeURIComponent(slug)}/">`;
      if (/<base\s/i.test(html)) {
        return html;
      }
      if (/<head(\s[^>]*)?>/i.test(html)) {
        return html.replace(/<head(\s[^>]*)?>/i, (match) => `${match}\n  ${base}`);
      }
      return `${base}\n${html}`;
    },
    openPreviewTab() {
      this.updatePreview();
      const blob = new Blob([this.previewHtml], { type: "text/html;charset=utf-8" });
      const previewUrl = URL.createObjectURL(blob);
      const previewWindow = window.open(previewUrl, "_blank");
      window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60000);
      if (!previewWindow) {
        this.snackbar = {
          show: true,
          text: "O navegador bloqueou a nova guia. Permita pop-ups para abrir o preview.",
        };
      } else {
        previewWindow.opener = null;
      }
    },
    toggleSplitPreview() {
      this.splitPreview = !this.splitPreview;
      if (this.splitPreview) {
        this.updatePreview();
      }
      this.$nextTick(() => {
        if (this.editor) {
          this.editor.refresh();
        }
      });
    },
    openSaveAs() {
      const version = this.nextVersion(this.currentItem.versao || "v1");
      this.saveForm = {
        titulo: `${this.currentItem.titulo || this.selectedSlug} - ${version}`,
        slug: `${this.selectedSlug}-${version}`,
        descricao: this.currentItem.descricao || "",
        versao: version,
        tipo: this.currentItem.tipo || "Apresentacao",
      };
      this.saveDialog = true;
    },
    async saveAs() {
      this.saving = true;
      this.error = "";
      try {
        const payload = {
          sourceSlug: this.selectedSlug,
          slug: this.saveForm.slug,
          titulo: this.saveForm.titulo,
          descricao: this.saveForm.descricao,
          versao: this.saveForm.versao,
          tipo: this.saveForm.tipo,
          html: this.html,
        };
        const response = await this.$http.post("/api/apresentacoes-editor/salvar-como", payload);
        this.saveDialog = false;
        this.snackbar = {
          show: true,
          text: "Nova versao salva.",
        };
        await this.loadManifest();
        this.selectedSlug = response.data.slug;
        await this.loadPresentation();
      } catch (err) {
        this.error = this.extractError(err, "Nao foi possivel salvar a nova versao.");
      } finally {
        this.saving = false;
      }
    },
    async save() {
      this.saving = true;
      this.error = "";
      try {
        await this.$http.put(`/api/apresentacoes-editor/${this.selectedSlug}`, {
          html: this.html,
        });
        this.snackbar = {
          show: true,
          text: "Apresentacao salva.",
        };
        await this.loadManifest(false);
      } catch (err) {
        this.error = this.extractError(err, "Nao foi possivel salvar a apresentacao.");
      } finally {
        this.saving = false;
      }
    },
    async downloadZip() {
      this.downloading = true;
      this.error = "";
      try {
        const response = await this.$http.get(
          `/api/apresentacoes-editor/${this.selectedSlug}/download`,
          { responseType: "blob" }
        );
        saveFile(response.data, `${this.selectedSlug}.zip`);
      } catch (err) {
        this.error = this.extractError(err, "Nao foi possivel baixar o ZIP.");
      } finally {
        this.downloading = false;
      }
    },
    async uploadZip(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) {
        return;
      }

      this.uploading = true;
      this.error = "";
      try {
        const form = new FormData();
        form.append("file", file);
        await this.$http.post(
          `/api/apresentacoes-editor/${this.selectedSlug}/upload`,
          form
        );
        await this.loadPresentation();
        this.snackbar = {
          show: true,
          text: "ZIP enviado e apresentacao atualizada.",
        };
      } catch (err) {
        this.error = this.extractError(err, "Nao foi possivel enviar o ZIP.");
      } finally {
        this.uploading = false;
      }
    },
    async copyHtml() {
      await navigator.clipboard.writeText(this.html);
      this.snackbar = {
        show: true,
        text: "HTML copiado.",
      };
    },
    nextVersion(version) {
      const match = String(version).match(/v(\d+)/i);
      if (!match) {
        return "v2";
      }
      return `v${Number(match[1]) + 1}`;
    },
    extractError(err, fallback) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string" ? err.response.data : fallback;
      }
      return fallback;
    },
  },
};
</script>

<style scoped>
.presentation-editor-page {
  min-height: 100vh;
  background: #eef4f6;
  padding: 28px;
}

.editor-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 20px;
}

.eyebrow {
  margin: 0 0 6px;
  color: #087b73;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}

h1 {
  margin: 0;
  color: #0b2d32;
  font-size: 34px;
  line-height: 1.05;
}

.subtitle {
  margin: 8px 0 0;
  color: #536a70;
  font-size: 16px;
}

.header-actions,
.toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.editor-toolbar {
  margin-bottom: 12px;
}

.workspace {
  height: 72vh;
  min-height: 620px;
}

.pane {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.pane-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 42px;
  padding: 0 12px;
  background: #102d34;
  color: #fff;
  border-radius: 8px 8px 0 0;
  font-weight: 800;
}

.pane-title small {
  color: #b9d4d7;
  font-weight: 700;
}

.preview-shell {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #d6e2e5;
  border-top: 0;
  border-radius: 0 0 8px 8px;
}

.preview-shell iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #fff;
}

.diagnostics {
  margin-top: 12px;
}

.diagnostic-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-radius: 8px;
  padding: 14px 16px;
}

.diagnostic-card span {
  color: #536a70;
  font-weight: 700;
}

.diagnostic-card strong {
  color: #0b2d32;
  font-size: 20px;
}

::v-deep .CodeMirror {
  flex: none;
  min-height: 0;
  height: calc(100% - 42px);
  border-radius: 0 0 8px 8px;
  font-family: Consolas, "Courier New", monospace;
  font-size: 14px;
}

@media (max-width: 960px) {
  .presentation-editor-page {
    padding: 18px;
  }

  .editor-header {
    align-items: stretch;
    flex-direction: column;
  }

  .header-actions,
  .toolbar-actions {
    justify-content: flex-start;
  }

  .workspace,
  .workspace--split {
    height: auto;
    min-height: 0;
  }

  .pane {
    height: 620px;
  }
}
</style>
